/**
 * PATH: packages/email-hub/src/feature/referrals/ReferToOrgAction.tsx
 * FILE_ID: EMAILHUB-REFER-TO-ORG-ACTION-001
 * PURPOSE: Explicitly refer one visible email into another member org.
 * WHY: Org-visible referral is distinct from the private board/bookmark flow.
 */
import { ExternalLink, Forward, Loader2, X } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { useApiAuth } from '../../_internal/components/ApiAuthGate'
import { useToast } from '../../_internal/components/Toast'
import { openEmailInOrg } from '../cross-org-actions/openEmailInOrg'
import { usePersonalMembershipCount } from '../personal/usePersonalData'
import { useCreateReferral, useReferralTargets } from './useReferralData'

interface ReferToOrgActionProps {
  emailId: string
  onChanged?: () => void
}

export function ReferToOrgAction({ emailId, onChanged }: ReferToOrgActionProps) {
  const membershipCount = usePersonalMembershipCount()
  const [open, setOpen] = useState(false)
  const [targetOrgId, setTargetOrgId] = useState<number | null>(null)
  const [note, setNote] = useState('')
  const closeButtonRef = useRef<HTMLButtonElement>(null)
  const targets = useReferralTargets(emailId, open)
  const createReferral = useCreateReferral()
  const { toast } = useToast()
  const navigate = useNavigate()
  const { session, switchOrganisation } = useApiAuth()

  useEffect(() => {
    if (!open) return
    closeButtonRef.current?.focus()
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false)
    }
    window.addEventListener('keydown', closeOnEscape)
    return () => window.removeEventListener('keydown', closeOnEscape)
  }, [open])

  if ((membershipCount.data ?? 0) < 2) return null

  const submit = async () => {
    if (targetOrgId == null) return
    try {
      const referral = await createReferral.mutateAsync({
        emailId,
        targetOrgId,
        note,
      })
      toast(`Referred to ${referral.target_org_name}`, 'success')
      setOpen(false)
      setTargetOrgId(null)
      setNote('')
      onChanged?.()
    } catch (error) {
      toast(
        error instanceof Error ? error.message : 'The email could not be referred.',
        'error',
      )
    }
  }

  const openTargetCopy = async (
    targetEmailId: string,
    targetOrgId: number,
    targetOrgName: string,
  ) => {
    const outcome = await openEmailInOrg({
      emailId: targetEmailId,
      targetOrgId,
      targetOrgName,
      currentOrgId: session?.organisation?.id ?? null,
      switchOrganisation,
      navigate,
    })
    if (outcome === 'switch-unavailable') {
      toast('Open this email from the organisation switcher first.', 'info')
      return
    }
    setOpen(false)
  }

  return (
    <>
      <button
        type="button"
        onClick={(event) => {
          event.stopPropagation()
          setOpen(true)
        }}
        className="inline-flex min-h-7 items-center gap-1 rounded-full border border-border px-2 text-[10px] font-medium text-muted-foreground hover:bg-muted hover:text-foreground"
        aria-label="Refer email to another organisation"
      >
        <Forward className="h-3 w-3" />
        Refer
      </button>

      {open && (
        <div
          className="fixed inset-0 z-[90] flex items-center justify-center bg-black/45 p-4"
          onClick={(event) => {
            event.stopPropagation()
            if (event.target === event.currentTarget) setOpen(false)
          }}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby={`refer-title-${emailId}`}
            className="w-full max-w-md rounded-xl border border-border bg-card p-5 shadow-2xl"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-3">
              <div>
                <h2 id={`refer-title-${emailId}`} className="text-base font-semibold">
                  Refer to organisation
                </h2>
                <p className="mt-1 text-xs text-muted-foreground">
                  The email stays in its source org and enters the target's gated
                  Referred inbox.
                </p>
              </div>
              <button
                ref={closeButtonRef}
                type="button"
                onClick={() => setOpen(false)}
                aria-label="Close referral dialog"
                className="rounded-md p-1 text-muted-foreground hover:bg-muted"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="mt-4 space-y-2">
              {targets.isLoading && (
                <p className="flex items-center gap-2 text-xs text-muted-foreground">
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  Loading organisations…
                </p>
              )}
              {targets.isError && (
                <button
                  type="button"
                  onClick={() => void targets.refetch()}
                  className="text-xs font-medium text-primary hover:underline"
                >
                  Retry organisations
                </button>
              )}
              {targets.data?.map((target) => {
                const unavailable =
                  !target.has_transfer_grant || target.has_pending_referral
                return (
                  <div
                    key={target.org_id}
                    className="flex min-h-12 items-stretch rounded-lg border border-border"
                  >
                    <button
                      type="button"
                      disabled={unavailable}
                      onClick={() => setTargetOrgId(target.org_id)}
                      aria-pressed={targetOrgId === target.org_id}
                      className="flex min-w-0 flex-1 items-center justify-between gap-2 rounded-l-lg px-3 text-left text-sm hover:bg-muted disabled:cursor-not-allowed disabled:opacity-55 aria-pressed:bg-primary/5"
                    >
                      <span className="truncate font-medium">{target.org_name}</span>
                      <span className="shrink-0 text-[11px] text-muted-foreground">
                        {target.has_pending_referral
                          ? 'Already pending'
                          : target.has_transfer_grant
                            ? target.is_suggested ? 'Suggested' : 'Available'
                            : 'Grant required'}
                      </span>
                    </button>
                    {target.target_email_id && (
                      <button
                        type="button"
                        onClick={() => void openTargetCopy(
                          target.target_email_id!,
                          target.org_id,
                          target.org_name,
                        )}
                        className="inline-flex shrink-0 items-center gap-1 border-l border-border px-2 text-[11px] font-medium text-primary hover:bg-primary/5"
                        title={`Switch to ${target.org_name} and open its visible copy`}
                      >
                        <ExternalLink className="h-3 w-3" />
                        {session?.organisation?.id === target.org_id
                          ? 'Open copy'
                          : 'Switch & open'}
                      </button>
                    )}
                  </div>
                )
              })}
              {targets.data?.length === 0 && (
                <p className="text-xs text-muted-foreground">
                  No other organisation memberships are available.
                </p>
              )}
            </div>

            <label className="mt-4 block text-xs font-medium">
              Note (optional)
              <textarea
                value={note}
                onChange={(event) => setNote(event.target.value)}
                maxLength={2000}
                rows={3}
                className="mt-1 w-full resize-y rounded-lg border border-border bg-background px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary/30"
                placeholder="Why does this belong with the target organisation?"
              />
            </label>

            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="min-h-9 rounded-lg border border-border px-3 text-sm font-medium"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={targetOrgId == null || createReferral.isPending}
                onClick={() => void submit()}
                className="inline-flex min-h-9 items-center gap-2 rounded-lg bg-primary px-3 text-sm font-medium text-primary-foreground disabled:opacity-50"
              >
                {createReferral.isPending && (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                )}
                Refer email
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
